import { ApiClient, CreationStatus, FileResponse } from '../../api/client';
import { BestMomentsProcessingState } from '../creation/BestMomentsProcessingState';
import { FailedState } from '../creation/FailedState';
import { PaymentRequired } from '../creation/PaymentRequired';
import { DraftCreationHandler } from '../creation/DraftCreationHandler';
import { UserInfo } from '../../types';
import { useState, useEffect, useCallback } from 'react';
import { SendToTelegramButton } from '../common/SendToTelegramButton';
import { OwnerInfo } from '../creation/OwnerInfo';
import { useTranslation } from 'react-i18next';
import { debugLog } from '../../utils/logger';

// Interface for the Best Moment metadata
interface BestMomentData {
  viral_score: number;
  hook_rating: string;
  flow_rating: string;
  value_rating: string;
  shareability_rating: string;
  hook_explanation: string;
  flow_explanation: string;
  value_explanation: string;
  shareability_explanation: string;
  trend_alignment: string;
  audience_engagement_potential: string;
  quality_score: number;
  quality_explanation: string;
  text: string;
  start_time: number;
  end_time: number;
  duration: number;
  reason: string;
  chunk_id: number;
  file_name: string;
  original_words: string[];
  title?: string;
  is_starred?: boolean;
  is_hidden?: boolean;
  original_index?: number; // Store the original index for API operations
}

// Interface for API response that may include starred/hidden status
interface BestMomentApiData extends Omit<BestMomentData, 'is_starred' | 'is_hidden' | 'original_index'> {
  is_starred?: boolean;
  is_hidden?: boolean;
}

export interface StrategyContentProps {
  creation: Awaited<ReturnType<typeof ApiClient.getCreation>>;
  userInfo: UserInfo;
  inputVideo?: FileResponse | undefined;
  outputVideo: FileResponse | undefined;
  onDownload: (url: string, filename: string) => void;
  onPaymentComplete: (newBalance: number) => void;
  creationId: string;
}

/**
 * BestMomentsStrategy component
 * 
 * This component handles the UI for the best moments strategy.
 * It displays different content based on the creation status.
 */
export function BestMomentsStrategy({
  creation,
  userInfo,
  onPaymentComplete,
  creationId
}: StrategyContentProps) {
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const { t } = useTranslation();

  const fetchCalculatedPrice = useCallback(async () => {
    try {
      const data = await ApiClient.getCreationPrice(creationId);
      setCalculatedPrice(data.price);
    } catch (error) {
      console.error('Error fetching price:', error);
    }
  }, [creationId]);

  useEffect(() => {
    // Fetch the calculated price when the creation is waiting for payment
    if (creation.status === CreationStatus.WAITING_FOR_PAYMENT) {
      fetchCalculatedPrice();
    }
  }, [creation.status, creationId, fetchCalculatedPrice]);

  // Handle draft status with the dedicated component
  if (creation.status === CreationStatus.DRAFT) {
    return (
      <DraftCreationHandler
        creation={creation}
        userInfo={userInfo}
        creationId={creationId}
        strategyType="best_moments"
        onUploadComplete={() => {
          // Fetch calculated price and refresh the creation data after upload
          fetchCalculatedPrice();
          window.location.reload();
        }}
        onPaymentComplete={onPaymentComplete}
        pricingInfo={t('draftCreationHandler.defaultPricingInfo')}
      />
    );
  }

  if (creation.status === CreationStatus.FAILED) {
    return <FailedState onRetry={() => window.location.reload()} />;
  }

  if (creation.status === CreationStatus.WAITING_FOR_PAYMENT) {
    return (
      <PaymentRequired
        creationId={creationId}
        processingPrice={calculatedPrice !== null ? calculatedPrice : creation.strategy.price}
        currentBalance={userInfo.balance}
        onPaymentComplete={onPaymentComplete}
        pricingInfo={t('draftCreationHandler.defaultPricingInfo')}
        strategyType="best_moments"
      />
    );
  }

  if (creation.status === CreationStatus.COMPLETED) {
    // Display best moments results
    return <BestMomentsResults 
      creation={creation} 
      creationId={creationId} 
      isOwner={!creation.owner} 
    />;
  }

  if (creation.status === CreationStatus.IN_PROGRESS || creation.status === CreationStatus.PENDING) {
    return <BestMomentsProcessingState 
      creation={creation} 
      onStatusChange={(updatedCreation) => {
        // No need to reload the page since the parent component will handle updates
        debugLog('Processing completed:', updatedCreation.status);
      }}
    />;
  }

  return null;
}

/**
 * BestMomentsResults component
 * 
 * Displays the extracted best moments with their metadata and video players
 */
function BestMomentsResults({ 
  creation, 
  creationId,
  isOwner
}: { 
  creation: Awaited<ReturnType<typeof ApiClient.getCreation>>; 
  creationId: string;
  isOwner: boolean;
}) {
  // Instead of storing the array index, store the original_index of the moment
  const [selectedMomentOriginalIndex, setSelectedMomentOriginalIndex] = useState<number | null>(0);
  const [showDetails, setShowDetails] = useState(false);
  const [bestMoments, setBestMoments] = useState<BestMomentData[]>([]);
  const [hiddenMomentsOpen, setHiddenMomentsOpen] = useState(false);
  const { t } = useTranslation();
  
  // Initialize and fetch best moments data
  useEffect(() => {
    try {
      if (creation.metadata?.best_moments) {
        // Add original index and preserve any existing starred/hidden status
        const moments = creation.metadata.best_moments.map((moment, index) => {
          const apiMoment = moment as BestMomentApiData;
          return {
            ...moment,
            is_starred: apiMoment.is_starred ?? false,
            is_hidden: apiMoment.is_hidden ?? false,
            original_index: index
          };
        });
        
        // Sort moments based on their starred/hidden status
        if (moments.length > 0) {
          const sortedMoments = sortMoments(moments);
          setBestMoments(sortedMoments);
        } else {
          setBestMoments([]);
        }
      }
    } catch (error) {
      console.error("Error initializing best moments:", error);
      setBestMoments([]);
    }
  }, [creation.metadata?.best_moments]);
  
  // Get the currently selected moment object (if any)
  const selectedMoment = selectedMomentOriginalIndex !== null 
    ? bestMoments.find(m => m.original_index === selectedMomentOriginalIndex) || null
    : null;

  // Toggle star status for a moment
  const handleStarToggle = async (originalIndex: number) => {
    if (!isOwner) return; // Only owner can star moments

    try {
      // Find moment by original_index
      const momentIndex = bestMoments.findIndex(m => m.original_index === originalIndex);
      if (momentIndex === -1) return;
      
      const moment = bestMoments[momentIndex];
      const newStarredStatus = !moment.is_starred;
      
      // Update in API
      await ApiClient.updateBestMoment(
        creationId,
        moment.original_index!,
        { is_starred: newStarredStatus }
      );
      
      // Update local state
      const updatedMoments = [...bestMoments];
      updatedMoments[momentIndex] = {
        ...updatedMoments[momentIndex],
        is_starred: newStarredStatus
      };
      
      // Sort moments: starred first, then non-starred, maintaining original order within groups
      const sortedMoments = sortMoments(updatedMoments);
      setBestMoments(sortedMoments);
    } catch (error) {
      console.error('Error updating starred status:', error);
    }
  };

  // Toggle hide status for a moment
  const handleHideToggle = async (originalIndex: number) => {
    if (!isOwner) return; // Only owner can hide moments

    try {
      // Find moment by original_index
      const momentIndex = bestMoments.findIndex(m => m.original_index === originalIndex);
      if (momentIndex === -1) return;
      
      const moment = bestMoments[momentIndex];
      const newHiddenStatus = !moment.is_hidden;
      
      // Update in API
      await ApiClient.updateBestMoment(
        creationId,
        moment.original_index!,
        { is_hidden: newHiddenStatus }
      );
      
      // Update local state
      const updatedMoments = [...bestMoments];
      updatedMoments[momentIndex] = {
        ...updatedMoments[momentIndex],
        is_hidden: newHiddenStatus
      };
      
      // Sort moments: starred first, then non-starred, maintaining original order within groups
      const sortedMoments = sortMoments(updatedMoments);
      setBestMoments(sortedMoments);

      // If hiding the currently selected moment, close the modal
      if (selectedMomentOriginalIndex === originalIndex && newHiddenStatus) {
        setSelectedMomentOriginalIndex(null);
        setShowDetails(false);
      }
    } catch (error) {
      console.error('Error updating hidden status:', error);
    }
  };

  // Sort moments: starred first, then non-hidden, maintaining original order within groups
  const sortMoments = (moments: BestMomentData[]): BestMomentData[] => {
    if (!moments || moments.length === 0) return [];
    
    return [...moments].sort((a, b) => {
      if (!a || !b) return 0;
      
      // First consider starred status
      if ((a.is_starred ?? false) && !(b.is_starred ?? false)) return -1;
      if (!(a.is_starred ?? false) && (b.is_starred ?? false)) return 1;
      
      // Then consider hidden status (shown before hidden)
      if (!(a.is_hidden ?? false) && (b.is_hidden ?? false)) return -1;
      if ((a.is_hidden ?? false) && !(b.is_hidden ?? false)) return 1;
      
      // If both have same starred and hidden status, maintain original order
      return ((a.original_index ?? 0) - (b.original_index ?? 0));
    });
  };
  
  // Function to render quality rating with appropriate styling
  const renderQualityIndicator = (rating: string) => {
    let color;
    let bgColor;
    
    if (rating === 'Excellent') {
      color = "text-green-600";
      bgColor = "bg-green-100 dark:bg-green-900/30";
    } else if (rating === 'Very Good') {
      color = "text-emerald-600";
      bgColor = "bg-emerald-100 dark:bg-emerald-900/30";
    } else if (rating === 'Good') {
      color = "text-blue-600";
      bgColor = "bg-blue-100 dark:bg-blue-900/30";
    } else if (rating === 'Average') {
      color = "text-yellow-600";
      bgColor = "bg-yellow-100 dark:bg-yellow-900/30";
    } else {
      color = "text-red-600";
      bgColor = "bg-red-100 dark:bg-red-900/30";
    }
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${color} ${bgColor}`}>
        {rating}
      </span>
    );
  };

  // Function to handle clicking on a moment
  const handleMomentClick = (originalIndex: number) => {
    if (originalIndex !== undefined && originalIndex !== null) {
      setSelectedMomentOriginalIndex(originalIndex);
      setShowDetails(true);
    }
  };

  // Function to close details view
  const handleCloseDetails = () => {
    setShowDetails(false);
  };

  // Make sure fileMap is safely initialized
  useEffect(() => {
    // Create file map when creation.result_files changes
    if (creation.result_files && creation.result_files.length > 0) {
      const newFileMap = new Map<string, FileResponse>();
      creation.result_files.forEach(file => {
        if (file && file.name) {
          newFileMap.set(file.name, file);
        }
      });
    }
  }, [creation.result_files]);
  
  // Map file names to file response objects
  const fileMap = new Map<string, FileResponse>();
  if (creation.result_files) {
    creation.result_files.forEach(file => {
      if (file && file.name) {
        fileMap.set(file.name, file);
      }
    });
  }
  
  if (!bestMoments || bestMoments.length === 0) {
    return (
      <div className="text-center p-6 bg-surface-interactive dark:bg-surface-interactive-dark border border-interactive dark:border-interactive-dark rounded-2xl shadow-interactive">
        <h2 className="text-xl font-bold text-content-strong mb-4">
          {creation.name ? t('bestMomentsStrategy.processingCompleteTitle', { name: creation.name }) : t('bestMomentsStrategy.defaultProcessingCompleteTitle')}
        </h2>
        <p className="text-theme-hint mb-6">
          {t('bestMomentsStrategy.noMomentsExtracted')}
        </p>
      </div>
    );
  }
  
  // Count visible and hidden moments
  const hiddenMoments = bestMoments.filter(moment => moment && moment.is_hidden);

  // Filter moments for display
  const displayedMoments = bestMoments.filter(moment => 
    moment && (!moment.is_hidden || (hiddenMomentsOpen && moment.is_hidden))
  );

  return (
    <div className="space-y-4">
      <div className="px-4 py-4">
        <h2 className="text-xl font-bold text-content-strong">
          {creation.name || t('bestMomentsStrategy.defaultResultsTitle')}
        </h2>
        <OwnerInfo owner={creation.owner} isShared={creation.is_shared} />
        <p className="text-theme-hint mt-2">
          {t('bestMomentsStrategy.extractedCountText', { count: bestMoments.length })}
          {isOwner && hiddenMoments.length > 0 && t('bestMomentsStrategy.hiddenCountSuffix', { count: hiddenMoments.length })}
        </p>
      </div>
      
      {/* Moments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayedMoments.map((moment) => {
          const file = fileMap.get(moment.file_name);
          if (!file) return null;
          
          return (
            <div 
              key={moment.original_index}
              className={`bg-surface rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border ${moment.is_hidden ? 'opacity-70' : ''}`}
              style={{ borderColor: 'var(--color-border)' }}
            >
              {/* Video thumbnail */}
              <div 
                className="relative aspect-video bg-media-canvas cursor-pointer"
                onClick={() => handleMomentClick(moment.original_index!)}
              >
                <video 
                  src={file.url}
                  poster={file.url + '?t=1'}
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="p-3 bg-buttonBg rounded-full text-buttonText shadow-md">
                    <span className="material-symbols-outlined text-xl">play_arrow</span>
                  </div>
                </div>
                
                {/* Starred indicator */}
                {isOwner && moment.is_starred && (
                  <div className="absolute top-2 right-2 text-yellow-500">
                    <span className="material-symbols-outlined text-xl">star</span>
                  </div>
                )}

                {/* Hidden indicator */}
                {isOwner && moment.is_hidden && (
                  <div className="absolute top-2 left-2 text-red-500">
                    <span className="material-symbols-outlined text-xl">visibility_off</span>
                  </div>
                )}
              </div>
              
              {/* Moment info */}
              <div className="p-3">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-content-strong">
                    {moment.title || t('bestMomentsStrategy.defaultMomentTitle', { index: moment.original_index! + 1 })}
                  </h3>
                  <div className="flex items-center justify-center px-2 py-1 bg-accent text-on-accent rounded-full">
                    <span className="text-xs font-semibold">
                      {moment.viral_score}/100
                    </span>
                  </div>
                </div>
                
                <p className="text-xs text-theme-hint line-clamp-2 mb-2">{moment.reason}</p>
                
                <div className="flex flex-wrap gap-2 mt-2">
                  <div className="flex items-center gap-1 text-xs text-theme-hint px-2 py-1 bg-neutral-badge dark:bg-neutral-badge-dark rounded-full">
                    <span className="material-symbols-outlined text-xs">timer</span>
                    <span>{Math.round(moment.duration)}s</span>
                  </div>
                  
                  <div className="flex items-center gap-1 text-xs text-theme-hint px-2 py-1 bg-neutral-badge dark:bg-neutral-badge-dark rounded-full">
                    <span className="material-symbols-outlined text-xs">bolt</span>
                    <span>{moment.hook_rating}</span>
                  </div>
                  
                  <div className="flex items-center gap-1 text-xs text-theme-hint px-2 py-1 bg-neutral-badge dark:bg-neutral-badge-dark rounded-full">
                    <span className="material-symbols-outlined text-xs">share</span>
                    <span>{moment.shareability_rating}</span>
                  </div>
                </div>
                
                <div className="mt-2">
                  <SendToTelegramButton
                    creationId={creationId}
                    resultFileId={file.id}
                    momentTitle={moment.title || t('bestMomentsStrategy.defaultMomentTitle', { index: moment.original_index! + 1 })}
                    isShared={creation.is_shared}
                    strategyType="best_moments"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Hidden Moments dropdown button */}
      {isOwner && hiddenMoments.length > 0 && (
        <div className="mt-4 p-4 bg-neutral-soft dark:bg-neutral-soft-dark rounded-xl">
          <button
            onClick={() => setHiddenMomentsOpen(!hiddenMomentsOpen)}
            className="flex items-center justify-between w-full text-left text-body"
          >
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined">
                {hiddenMomentsOpen ? 'expand_less' : 'expand_more'}
              </span>
              <span className="font-medium">
                {t('bestMomentsStrategy.hiddenMomentsButton', { count: hiddenMoments.length })}
              </span>
            </div>
          </button>
        </div>
      )}
      
      {/* Details Modal */}
      {showDetails && selectedMoment && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-modal-overlay flex items-center justify-center p-4">
          <div 
            className="relative rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: 'var(--color-surface)' }}
          >
            {/* Header with close button */}
            <div 
              className="sticky top-0 p-4 flex justify-between items-center z-10"
              style={{ backgroundColor: 'var(--color-surface)' }}
            >
              <h2 className="text-lg font-semibold text-content-strong">
                {selectedMoment.title || t('bestMomentsStrategy.defaultMomentTitle', { index: selectedMoment.original_index! + 1 })}
              </h2>
              <div className="flex items-center gap-2">
                {/* Star/Unstar and Hide/Unhide buttons in modal - only for owner */}
                {isOwner && (
                  <>
                    <button
                      onClick={() => handleStarToggle(selectedMoment.original_index!)}
                      className={`p-2 rounded-full h-9 w-9 flex items-center justify-center active:brightness-90 transition-all duration-150 ease-in-out 
                        ${
                          selectedMoment.is_starred
                            ? 'text-yellow-400 bg-yellow-400/20 dark:bg-yellow-400/30 hover:bg-yellow-400/30 dark:hover:bg-yellow-400/40'
                            : 'text-muted bg-button-neutral hover:bg-button-neutral-hover'
                        }`}
                      title={selectedMoment.is_starred ? t('bestMomentsStrategy.unstarButtonTitle') : t('bestMomentsStrategy.starButtonTitle')}
                    >
                      <span className="material-symbols-outlined">
                        {selectedMoment.is_starred ? 'star' : 'star_outline'}
                      </span>
                    </button>
                    <button
                      onClick={() => handleHideToggle(selectedMoment.original_index!)}
                      className={`p-2 rounded-full h-9 w-9 flex items-center justify-center active:brightness-90 transition-all duration-150 ease-in-out 
                        ${
                          selectedMoment.is_hidden
                            ? 'text-red-500 bg-red-500/10 dark:bg-red-500/20 hover:bg-red-500/20 dark:hover:bg-red-500/30'
                            : 'text-muted bg-button-neutral hover:bg-button-neutral-hover'
                        }`}
                      title={selectedMoment.is_hidden ? t('bestMomentsStrategy.unhideButtonTitle') : t('bestMomentsStrategy.hideButtonTitle')}
                    >
                      <span className="material-symbols-outlined">
                        {selectedMoment.is_hidden ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                  </>
                )}
                <button 
                  onClick={handleCloseDetails}
                  className="p-2 rounded-full h-9 w-9 flex items-center justify-center active:brightness-90 transition-all duration-150 ease-in-out text-muted bg-button-neutral hover:bg-button-neutral-hover"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>
            
            {/* Responsive layout - Flex column on mobile, row on desktop */}
            <div className="lg:flex lg:flex-row lg:gap-6 p-4">
              {/* Left column (video) - Fixed width on desktop */}
              <div className="lg:w-[45%] lg:flex-shrink-0 mb-6 lg:mb-0">
                {/* VIDEO SECTION */}
                <div className="sticky lg:top-20">
                  {selectedMoment.file_name && fileMap.get(selectedMoment.file_name) && (
                    <video 
                      controls 
                      autoPlay
                      className="w-full rounded-lg max-h-[70vh] object-contain bg-media-canvas"
                      src={fileMap.get(selectedMoment.file_name)!.url}
                    />
                  )}
                  {/* Brief summary underneath video */}
                  <div 
                    className="mt-4 p-4 bg-neutral-soft dark:bg-neutral-soft-dark border rounded-lg border-default"
                  >
                    <p className="text-sm text-body-emphasis">{selectedMoment.reason}</p>
                  </div>
                  {/* Inline Send to Telegram button */}
                  {selectedMoment.file_name && fileMap.get(selectedMoment.file_name) && (
                  <SendToTelegramButton
                    creationId={creationId}
                    resultFileId={fileMap.get(selectedMoment.file_name)!.id}
                    momentTitle={selectedMoment.title || t('bestMomentsStrategy.defaultMomentTitle', { index: selectedMoment.original_index! + 1 })}
                    isShared={creation.is_shared}
                    strategyType="best_moments"
                  />
                  )}
                </div>
              </div>
              
              {/* Right column (details) - Takes remaining space on desktop */}
              <div className="lg:flex-1 lg:overflow-y-auto">
                {/* SCORING SECTION */}
                <section className="mb-6">
                  <h3 className="text-lg font-medium text-content-strong mb-4">{t('bestMomentsStrategy.scoringSectionTitle')}</h3>
                  
                  {/* Viral score */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-yellow-500">trending_up</span>
                        <h4 className="font-medium text-content-strong">{t('bestMomentsStrategy.viralScoreLabel')}</h4>
                      </div>
                      <span className="font-bold text-yellow-500">{selectedMoment.viral_score}/100</span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-yellow-400 to-orange-500"
                        style={{ width: `${selectedMoment.viral_score}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Quality Ratings - Responsive layout */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div 
                      className="bg-surface p-3 rounded-lg border"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-blue-500">bolt</span>
                          <h4 className="font-medium text-sm text-content-strong">{t('bestMomentsStrategy.hookLabel')}</h4>
                        </div>
                        {renderQualityIndicator(selectedMoment.hook_rating)}
                      </div>
                      <p className="text-xs text-theme-hint hidden md:block">{selectedMoment.hook_explanation}</p>
                    </div>
                    
                    <div 
                      className="bg-surface p-3 rounded-lg border"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-purple-500">waves</span>
                          <h4 className="font-medium text-sm text-content-strong">{t('bestMomentsStrategy.flowLabel')}</h4>
                        </div>
                        {renderQualityIndicator(selectedMoment.flow_rating)}
                      </div>
                      <p className="text-xs text-theme-hint hidden md:block">{selectedMoment.flow_explanation}</p>
                    </div>
                    
                    <div 
                      className="bg-surface p-3 rounded-lg border"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-green-500">insights</span>
                          <h4 className="font-medium text-sm text-content-strong">{t('bestMomentsStrategy.valueLabel')}</h4>
                        </div>
                        {renderQualityIndicator(selectedMoment.value_rating)}
                      </div>
                      <p className="text-xs text-theme-hint hidden md:block">{selectedMoment.value_explanation}</p>
                    </div>
                    
                    <div 
                      className="bg-surface p-3 rounded-lg border"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-orange-500">share</span>
                          <h4 className="font-medium text-sm text-content-strong">{t('bestMomentsStrategy.shareabilityLabel')}</h4>
                        </div>
                        {renderQualityIndicator(selectedMoment.shareability_rating)}
                      </div>
                      <p className="text-xs text-theme-hint hidden md:block">{selectedMoment.shareability_explanation}</p>
                    </div>
                  </div>
                </section>
                
                {/* TRANSCRIPT SECTION */}
                <section className="mb-6">
                  <h3 className="text-lg font-medium text-content-strong mb-4">{t('bestMomentsStrategy.transcriptSectionTitle')}</h3>
                  <div 
                    className="bg-surface p-4 rounded-lg border"
                    style={{ borderColor: 'var(--color-border)' }}
                  >
                    <p className="text-sm text-theme-hint whitespace-pre-wrap">{selectedMoment.text}</p>
                  </div>
                </section>
                
                {/* REASONING SECTION */}
                <section>
                  <h3 className="text-lg font-medium text-content-strong mb-4">{t('bestMomentsStrategy.analysisSectionTitle')}</h3>
                  
                  {/* Detailed explanations */}
                  <div className="mb-4">
                    <h4 className="text-md font-medium text-content-strong mb-3">{t('bestMomentsStrategy.qualityMetricsExplained')}</h4>
                    <div className="space-y-3">
                      <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/20 via-blue-400/10 to-blue-500/5 border border-blue-500/30">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="material-symbols-outlined text-blue-500">bolt</span>
                          <h5 className="font-medium text-blue-700 dark:text-blue-300">{t('bestMomentsStrategy.hookRatingLabel', { rating: selectedMoment.hook_rating })}</h5>
                        </div>
                        <p className="text-sm text-theme-hint">{selectedMoment.hook_explanation}</p>
                      </div>
                      
                      <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500/20 via-purple-400/10 to-purple-500/5 border border-purple-500/30">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="material-symbols-outlined text-purple-500">waves</span>
                          <h5 className="font-medium text-purple-700 dark:text-purple-300">{t('bestMomentsStrategy.flowRatingLabel', { rating: selectedMoment.flow_rating })}</h5>
                        </div>
                        <p className="text-sm text-theme-hint">{selectedMoment.flow_explanation}</p>
                      </div>
                      
                      <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/20 via-green-400/10 to-green-500/5 border border-green-500/30">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="material-symbols-outlined text-green-500">insights</span>
                          <h5 className="font-medium text-green-700 dark:text-green-300">{t('bestMomentsStrategy.valueRatingLabel', { rating: selectedMoment.value_rating })}</h5>
                        </div>
                        <p className="text-sm text-theme-hint">{selectedMoment.value_explanation}</p>
                      </div>
                      
                      <div className="p-3 rounded-lg bg-gradient-to-br from-orange-500/20 via-orange-400/10 to-orange-500/5 border border-orange-500/30">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="material-symbols-outlined text-orange-500">share</span>
                          <h5 className="font-medium text-orange-700 dark:text-orange-300">{t('bestMomentsStrategy.shareabilityRatingLabel', { rating: selectedMoment.shareability_rating })}</h5>
                        </div>
                        <p className="text-sm text-theme-hint">{selectedMoment.shareability_explanation}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Additional insights */}
                  <div className="mb-4">
                    <h4 className="text-md font-medium text-content-strong mb-3">{t('bestMomentsStrategy.marketingPotential')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div 
                        className="bg-surface p-3 rounded-lg border"
                        style={{ borderColor: 'var(--color-border)' }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="material-symbols-outlined text-content-strong">trending_up</span>
                          <h5 className="text-sm font-medium text-content-strong">{t('bestMomentsStrategy.trendAlignmentLabel')}</h5>
                        </div>
                        <p className="text-sm text-theme-hint">{selectedMoment.trend_alignment}</p>
                      </div>
                      
                      <div 
                        className="bg-surface p-3 rounded-lg border"
                        style={{ borderColor: 'var(--color-border)' }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="material-symbols-outlined text-content-strong">group</span>
                          <h5 className="text-sm font-medium text-content-strong">{t('bestMomentsStrategy.audienceEngagementLabel')}</h5>
                        </div>
                        <p className="text-sm text-theme-hint">{selectedMoment.audience_engagement_potential}</p>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 